'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { parseHttpRequest, parseHttpResponse } from '@terra/shared-utils';
import { toasterApi } from '../../toast/ToastProvider';
import { resolveReplayUrl } from '../http-replay/http-replay-url.utils';

export type HttpChatRequestData = {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  rawHeaders?: string[];
  body?: string;
  httpVersion: string;
};

export type HttpChatResponseData = {
  statusCode: number;
  statusText?: string;
  headers: Record<string, string | string[]>;
  rawHeaders?: string[];
  body?: string;
  httpVersion: string;
};

export type HttpSendToChatData = {
  source: 'request' | 'response';
  request?: HttpChatRequestData;
  response?: HttpChatResponseData;
};

export type SendRawHttpToChatInput = {
  content: string;
  selectedText?: string;
  requestContent?: string;
  responseContent?: string;
};

type HttpSendToChatContextType = {
  sendRawToChat: (input: SendRawHttpToChatInput) => void;
  isAvailable: boolean;
};

const HttpSendToChatContext = createContext<
  HttpSendToChatContextType | undefined
>(undefined);

type HttpSendToChatProviderProps = {
  children: ReactNode;
  onSendToChat?: (data: HttpSendToChatData) => void;
  onSendTextToChat?: (text: string) => void;
};

const isRawHttpResponse = (content: string): boolean =>
  content.trimStart().startsWith('HTTP/');

const toOptionalBody = (body: string): string | undefined =>
  body.trim() ? body : undefined;

const parseRawChatRequest = (
  content: string | undefined,
): HttpChatRequestData | undefined => {
  if (!content || isRawHttpResponse(content)) {
    return undefined;
  }

  const parsed = parseHttpRequest(content, { captureRawHeaders: true });

  if (!parsed) {
    return undefined;
  }

  return {
    method: parsed.method,
    url: resolveReplayUrl({ url: parsed.url, headers: parsed.headers }),
    headers: parsed.headers,
    rawHeaders: parsed.rawHeaders,
    body: toOptionalBody(parsed.body),
    httpVersion: parsed.httpVersion,
  };
};

const parseRawChatResponse = (
  content: string | undefined,
): HttpChatResponseData | undefined => {
  if (!content || !isRawHttpResponse(content)) {
    return undefined;
  }

  const parsed = parseHttpResponse(content, { captureRawHeaders: true });

  if (!parsed) {
    return undefined;
  }

  return {
    statusCode: parsed.statusCode,
    statusText: parsed.statusMessage || undefined,
    headers: parsed.headers,
    rawHeaders: parsed.rawHeaders,
    body: toOptionalBody(parsed.body),
    httpVersion: parsed.httpVersion,
  };
};

const buildSendToChatData = ({
  content,
  requestContent,
  responseContent,
}: SendRawHttpToChatInput): HttpSendToChatData | undefined => {
  if (isRawHttpResponse(content)) {
    const response = parseRawChatResponse(content);

    return response
      ? {
          source: 'response',
          response,
          request: parseRawChatRequest(requestContent),
        }
      : undefined;
  }

  const request = parseRawChatRequest(content);

  return request
    ? {
        source: 'request',
        request,
        response: parseRawChatResponse(responseContent),
      }
    : undefined;
};

export function HttpSendToChatProvider({
  children,
  onSendToChat,
  onSendTextToChat,
}: HttpSendToChatProviderProps) {
  const t = useTranslations('designSystem.chat.httpSendToChat');
  const contextValue: HttpSendToChatContextType = useMemo(
    () => ({
      sendRawToChat: (input: SendRawHttpToChatInput) => {
        if (!onSendToChat) {
          return;
        }

        if (input.selectedText && onSendTextToChat) {
          onSendTextToChat(input.selectedText);

          return;
        }

        const data = buildSendToChatData(input);

        if (data) {
          onSendToChat(data);

          return;
        }

        toasterApi.error({
          title: t('parseFailedTitle'),
          description: t('parseFailedDescription'),
        });
      },
      isAvailable: Boolean(onSendToChat),
    }),
    [onSendToChat, onSendTextToChat, t],
  );

  return (
    <HttpSendToChatContext.Provider value={contextValue}>
      {children}
    </HttpSendToChatContext.Provider>
  );
}

export function useSendHttpToChat():
  | ((input: SendRawHttpToChatInput) => void)
  | undefined {
  const context = useContext(HttpSendToChatContext);

  if (!context?.isAvailable) return undefined;

  return context.sendRawToChat;
}
